const reportedImpressions = new Set();
const observedCards = new Set();
let observer = null;
let initialized = false;
let privacyReady = false;
// BEGIN CUSTOM EDIT: Collection section navigation lifecycle | Date: 2026-09-16 | What: Pause impressions while a collection section swap/restoration is pending
let navigationPending = false;
// END CUSTOM EDIT

function analyticsAllowed() {
  const privacy = window.Shopify?.customerPrivacy;

  if (typeof privacy?.analyticsProcessingAllowed === "function") {
    return privacy.analyticsProcessingAllowed() === true;
  }

  return false;
}

function ensureCustomerPrivacyReady() {
  if (typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === "function") {
    privacyReady = true;
    return Promise.resolve(true);
  }

  if (typeof window.Shopify?.loadFeatures !== "function") {
    return Promise.resolve(false);
  }

  return new Promise((resolve) => {
    window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], (error) => {
      privacyReady = !error && typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === "function";
      resolve(privacyReady);
    });
  });
}

function hasCollectionMetadata(root = document) {
  return Boolean(root.querySelector?.("[data-collection-analytics-context][data-collection-analytics-list-id][data-collection-analytics-list-name]"));
}

function contextKey(context) {
  const url = new URL(window.location.href);
  url.searchParams.delete("page");
  url.searchParams.delete("section_id");
  url.searchParams.delete("sections");
  url.searchParams.delete("sections[]");

  return [
    context.dataset.collectionAnalyticsListId || "",
    context.dataset.collectionAnalyticsListHandle || "",
    url.pathname,
    url.searchParams.toString()
  ].join("|");
}

function readItem(card) {
  const script = card.querySelector('script[type="application/json"][data-collection-analytics-payload]');

  if (!script?.textContent?.trim()) {
    return null;
  }

  try {
    const item = JSON.parse(script.textContent);

    if (!item?.item_id || !item.item_name) {
      return null;
    }

    return item;
  } catch (error) {
    return null;
  }
}

function selectedVariantInput(card) {
  return card.querySelector('.product-card__swatch-list input[data-variant-id]:checked, .product-card__variant-list input[data-variant-id]:checked');
}

function itemForClick(card, link) {
  const item = readItem(card);

  if (!item) {
    return null;
  }

  const selectedVariant = selectedVariantInput(card);

  if (!selectedVariant) {
    return item;
  }

  const selectedItem = { ...item };
  const variantId = selectedVariant.dataset.variantId;
  const variantSku = selectedVariant.dataset.variantSku;
  const variantPrice = Number(selectedVariant.dataset.variantPrice);
  const variantUrl = selectedVariant.dataset.variantUrl;

  if (variantId) {
    selectedItem.item_variant = variantId;
  }

  if (variantSku !== undefined) {
    selectedItem.item_sku = variantSku;
  }

  if (Number.isFinite(variantPrice)) {
    selectedItem.price = variantPrice;
  }

  if (variantUrl) {
    selectedItem.url = variantUrl;
  } else {
    const linkUrl = new URL(link.href, window.location.href);
    selectedItem.url = `${linkUrl.pathname}${linkUrl.search}${linkUrl.hash}`;
  }

  return selectedItem;
}

function collectionContext(card) {
  const context = card.closest("[data-collection-analytics-context]");

  if (!context?.dataset.collectionAnalyticsListId || !context.dataset.collectionAnalyticsListName) {
    return null;
  }

  return {
    context,
    key: contextKey(context),
    item_list_id: context.dataset.collectionAnalyticsListId,
    item_list_name: context.dataset.collectionAnalyticsListName,
    currency: context.dataset.collectionAnalyticsCurrency || window.Shopify?.currency?.active || "EUR"
  };
}

function pushEcommerceEvent(eventName, context, items) {
  if (!privacyReady || !analyticsAllowed() || items.length === 0) {
    return false;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: eventName,
    ecommerce: {
      currency: context.currency,
      item_list_id: context.item_list_id,
      item_list_name: context.item_list_name,
      items
    }
  });

  return true;
}

function reportImpressions(cards) {
  // BEGIN CUSTOM EDIT: Collection section navigation lifecycle | Date: 2026-09-16 | What: Avoid transient first-batch impressions before a restored collection finishes loading
  if (navigationPending) {
    return;
  }
  // END CUSTOM EDIT

  const groups = new Map();

  cards.forEach((card) => {
    if (!card.isConnected) {
      observer?.unobserve(card);
      return;
    }

    const itemContext = collectionContext(card);
    const item = readItem(card);

    if (!itemContext || !item) {
      return;
    }

    const impressionKey = `${itemContext.key}|${item.item_id}`;

    if (reportedImpressions.has(impressionKey)) {
      return;
    }

    const group = groups.get(itemContext.key) || { context: itemContext, items: [] };
    group.items.push({ item, impressionKey, card });
    groups.set(itemContext.key, group);
  });

  groups.forEach(({ context, items }) => {
    if (pushEcommerceEvent("view_item_list_senkels", context, items.map(({ item }) => item))) {
      items.forEach(({ impressionKey, card }) => {
        reportedImpressions.add(impressionKey);
        unobserveCard(card);
      });
    }
  });
}

function visibleRatio(element) {
  const rect = element.getBoundingClientRect();

  if (rect.width <= 0 || rect.height <= 0) {
    return 0;
  }

  const visibleWidth = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
  const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));

  return visibleWidth * visibleHeight / (rect.width * rect.height);
}

function reportImpression(card) {
  const context = collectionContext(card);
  const item = readItem(card);

  if (!context || !item) {
    return;
  }

  const impressionKey = `${context.key}|${item.item_id}`;

  if (reportedImpressions.has(impressionKey)) {
    return;
  }

  reportImpressions([card]);
}

function isVisible(card) {
  return card.isConnected && visibleRatio(card) >= 0.5;
}

function registerCards(root = document) {
  const cards = Array.from(root.querySelectorAll?.("product-card[data-collection-analytics-card]") || []);

  cards.forEach((card) => {
    if (card.dataset.collectionAnalyticsRegistered === "true") {
      return;
    }

    card.dataset.collectionAnalyticsRegistered = "true";

    if (observer) {
      observeCard(card);
    } else if (isVisible(card)) {
      reportImpression(card);
    }
  });

  return cards.length;
}

function observeCard(card) {
  if (!observer || observedCards.has(card)) {
    return;
  }

  observedCards.add(card);
  observer.observe(card);
}

function unobserveCard(card) {
  if (!observer || !observedCards.has(card)) {
    return;
  }

  observedCards.delete(card);
  observer.unobserve(card);
}

function cleanupObservedCards(root = document) {
  observedCards.forEach((card) => {
    if (!card.isConnected || !root.contains(card)) {
      unobserveCard(card);
    }
  });
}

function registerVisibleUnreportedCards() {
  reportImpressions(Array.from(document.querySelectorAll("product-card[data-collection-analytics-card]")).filter(isVisible));
}

function onCardClick(event) {
  if (!event.isTrusted) {
    return;
  }

  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return;
  }

  const link = event.target.closest("a[href]");
  const card = event.target.closest("product-card[data-collection-analytics-card]");

  if (!link || !card || !card.contains(link)) {
    return;
  }

  const linkUrl = new URL(link.href, window.location.href);

  if (linkUrl.origin !== window.location.origin || !linkUrl.pathname.includes("/products/")) {
    return;
  }

  const context = collectionContext(card);
  const item = itemForClick(card, link);

  if (!context || !item) {
    return;
  }

  pushEcommerceEvent("select_item_senkels", context, [item]);
}

async function init() {
  if (initialized) {
    registerCards();
    return;
  }

  if (!hasCollectionMetadata()) {
    return;
  }

  if ("IntersectionObserver" in window) {
    observer = new IntersectionObserver((entries) => {
      reportImpressions(entries.filter((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5).map((entry) => entry.target));
    }, { threshold: 0.5 });
  }

  initialized = true;
  window.senkelsCollectionTracking = true;
  document.addEventListener("click", onCardClick, { capture: true });
  registerCards();

  if (await ensureCustomerPrivacyReady()) {
    registerVisibleUnreportedCards();
  }
}

document.addEventListener("collection:analytics:items-added", (event) => {
  registerCards(event.detail?.root || document);
});

document.addEventListener("facet:update:complete", (event) => {
  cleanupObservedCards(document.getElementById(`shopify-section-${event.detail?.sectionId}`) || document);

  if (event.detail?.success) {
    registerCards(document.getElementById(`shopify-section-${event.detail.sectionId}`) || document);
    registerVisibleUnreportedCards();
  }
});

// BEGIN CUSTOM EDIT: Collection section navigation lifecycle | Date: 2026-09-16 | What: Rebind collection analytics to section-rendered collection pages
document.addEventListener("collection:navigation:start", () => {
  navigationPending = true;
});

document.addEventListener("collection:navigation:complete", (event) => {
  navigationPending = false;

  if (event.detail?.success !== true) {
    registerVisibleUnreportedCards();
    return;
  }

  const section = document.getElementById(`shopify-section-${event.detail.sectionId}`) || document;
  reportedImpressions.clear();
  cleanupObservedCards(document);
  init();
  registerCards(section);
  registerVisibleUnreportedCards();
});
// END CUSTOM EDIT

document.addEventListener("visitorConsentCollected", () => {
  if (typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === "function") {
    privacyReady = true;
  }

  registerVisibleUnreportedCards();
});

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init, { once: true });
} else {
  init();
}
